import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipValueType } from 'recharts'
import { useId, useMemo } from 'react'
import type { NavPoint } from '@/types'

export interface BenchmarkSeries {
  dataKey: string
  label: string
  color?: string
  strokeDasharray?: string
}

interface Props {
  data: NavPoint[]
  color?: string
  valueKey?: keyof NavPoint
  seriesName?: string
  benchmarkSeries?: BenchmarkSeries
  extraSeries?: BenchmarkSeries[]
  valueSuffix?: string
  tooltipDigits?: number
  tooltipMode?: 'value' | 'growth'
}

export default function NavChart({
  data,
  color = '#3b82f6',
  valueKey = 'nav_value',
  seriesName = '策略',
  benchmarkSeries,
  extraSeries = [],
  valueSuffix = '',
  tooltipDigits = 4,
  tooltipMode = 'value',
}: Props) {
  const gradientId = useId()

  const chartData = useMemo(() => data, [data])

  if (!chartData.length) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#64748b',
          fontSize: 13,
        }}
      >
        暂无数据
      </div>
    )
  }

  const overlays = [
    ...(benchmarkSeries ? [benchmarkSeries] : []),
    ...extraSeries,
  ]

  const values = chartData
    .flatMap((item) => {
      const primary = item[valueKey]
      const overlayValues = overlays.map((series) => item[series.dataKey as keyof NavPoint])
      return [primary, ...overlayValues]
    })
    .filter((value): value is number => typeof value === 'number')
  const rawMinValue = Math.min(...values)
  const rawMaxValue = Math.max(...values)
  const valueRange = rawMaxValue - rawMinValue
  const valueMagnitude = Math.max(Math.abs(rawMinValue), Math.abs(rawMaxValue), 0.0001)
  const hasSmallVariance =
    valueRange > 0 && (valueRange <= 1 || valueRange / valueMagnitude < 0.05)

  const padding = valueRange === 0
    ? Math.max(valueMagnitude * 0.01, 0.01)
    : hasSmallVariance
      ? Math.max(valueRange * 0.08, 0.01)
      : Math.max(valueRange * 0.12, 0.1)

  const domain: [number, number] = hasSmallVariance
    ? [rawMinValue - padding, rawMaxValue + padding]
    : [Math.min(rawMinValue, 0) - padding, Math.max(rawMaxValue, 0) + padding]

  const benchmarkColor = benchmarkSeries?.color ?? 'rgba(148, 163, 184, 0.75)'

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 20 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="rgba(148, 163, 184, 0.08)" />
        <XAxis
          dataKey="trade_date"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 10, fill: '#64748b' }}
          angle={-35}
          textAnchor="end"
          height={50}
        />
        <YAxis
          domain={domain}
          allowDataOverflow
          axisLine={false}
          tickLine={false}
          tickFormatter={(v: number) => `${v.toFixed(valueSuffix ? 1 : 2)}${valueSuffix}`}
          tick={{ fontSize: 10, fill: color === '#3b82f6' ? '#64748b' : color }}
          width={valueSuffix ? 52 : 44}
        />
        <Tooltip
          formatter={(v: TooltipValueType | undefined, name) => {
            const numberValue = Number(v ?? 0)
            if (tooltipMode === 'growth') {
              const pct = (numberValue - 1) * 100
              const sign = pct > 0 ? '+' : ''
              return [`${numberValue.toFixed(4)}（${sign}${pct.toFixed(2)}%）`, String(name ?? '')]
            }
            return [`${numberValue.toFixed(tooltipDigits)}${valueSuffix}`, String(name ?? '')]
          }}
          labelFormatter={(label) => `日期 ${label}`}
          labelStyle={{ fontSize: 11, color: '#cbd5e1' }}
          contentStyle={{
            fontSize: 11,
            border: '1px solid rgba(148, 163, 184, 0.12)',
            borderRadius: 10,
            backgroundColor: '#111a2e',
            color: '#e2e8f0',
          }}
        />
        <Area
          type="monotone"
          dataKey={valueKey}
          name={seriesName}
          stroke={color}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={{ r: 3, fill: color, stroke: color, strokeWidth: 1 }}
          strokeWidth={2}
          isAnimationActive={false}
        />
        {overlays.map((series) => {
          const stroke = series.color ?? benchmarkColor
          return (
            <Line
              key={series.dataKey}
              type="monotone"
              dataKey={series.dataKey}
              name={series.label}
              stroke={stroke}
              strokeDasharray={series.strokeDasharray ?? '5 5'}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3, fill: stroke, stroke: stroke, strokeWidth: 1 }}
              isAnimationActive={false}
            />
          )
        })}
      </AreaChart>
    </ResponsiveContainer>
  )
}
